import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiRefreshCw, FiImage, FiUpload, FiX } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Container = styled.div`
  padding: 32px;
  max-width: 100%;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-top: 8px;
  margin: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  padding: 24px;
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
    margin: 0 -16px 16px -16px;
    border-radius: 0;
    border-left: none;
    border-right: none;
    width: calc(100% + 32px);
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ColorInputGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ColorInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ColorInputLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
`;

const ColorPickerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ColorInput = styled.input`
  width: 80px;
  height: 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  background: none;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
  
  &:hover {
    border-color: #5efba9;
  }
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const ColorTextInput = styled.input`
  flex: 1;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: monospace;
  color: #1f2937;
  
  &:hover {
    border-color: #d1d5db;
  }
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  
  &:hover {
    border-color: #d1d5db;
  }
  
  &:focus {
    outline: none;
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const PreviewSection = styled.div`
  margin-top: 24px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const PreviewLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
`;

const PreviewBanner = styled.div`
  background: ${props => props.$gradient || 'linear-gradient(135deg, #5efba9 0%, #34D399 100%)'};
  border-radius: 16px;
  padding: 48px 32px;
  text-align: center;
  color: white;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
`;

const PreviewBrandName = styled.h2`
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
`;

const PreviewDescription = styled.p`
  font-size: 16px;
  opacity: 0.95;
  margin: 0;
  color: white;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #5efba9;
  color: #1f2937;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  flex: 1;
  
  &:hover:not(:disabled) {
    background: #4dd1a0;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(94, 251, 169, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const SuccessMessage = styled.div`
  padding: 12px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(31, 41, 55, 0.3);
  border-radius: 50%;
  border-top-color: #1f2937;
  animation: spin 0.6s linear infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const InfoText = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
`;

const ImageUploadSection = styled.div`
  margin-bottom: 24px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const ImagePreview = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid #e5e7eb;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 8px;
`;

const ImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  color: #9ca3af;
  font-size: 48px;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(220, 38, 38, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  
  &:hover {
    background: rgba(220, 38, 38, 1);
    transform: scale(1.1);
  }
`;

const UploadButton = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  color: #374151;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  width: 100%;
  max-width: 300px;
  
  &:hover {
    background: #f9fafb;
    border-color: #5efba9;
    color: #5efba9;
  }
  
  input[type="file"] {
    display: none;
  }
`;

function AdminBrandSettings() {
  const { session } = useAuthContext();
  const { isArabic } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brand, setBrand] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [brandImage, setBrandImage] = useState('');
  const [imageError, setImageError] = useState(false);
  const fileInputRef = React.createRef();
  
  // Default colors (the current hardcoded gradient)
  const defaultColor1 = '#5efba9';
  const defaultColor2 = '#34D399';
  
  const [color1, setColor1] = useState(defaultColor1);
  const [color2, setColor2] = useState(defaultColor2);
  const [brandName, setBrandName] = useState('');

  useEffect(() => {
    if (session?.user?.id) {
      fetchBrand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const fetchBrand = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user's brand_id - try brand_managers first, then fallback to brands table
      let foundBrandId = null;
      
      // Try getting from brand_managers table
      const { data: brandManager } = await supabase
        .from('brand_managers')
        .select('brand_id')
        .eq('profile_id', session.user.id)
        .maybeSingle();

      if (brandManager && brandManager.brand_id) {
        foundBrandId = brandManager.brand_id;
      } else {
        // Fallback: Try to find brand where user is the creator
        const { data: userBrand } = await supabase
          .from('brands')
          .select('id')
          .eq('created_by', session.user.id)
          .maybeSingle();

        if (userBrand && userBrand.id) {
          foundBrandId = userBrand.id;
        }
      }

      if (!foundBrandId) {
        throw new Error(isArabic ? 'لم يتم العثور على علامة تجارية لهذا المستخدم.' : 'No brand found for this user.');
      }

      setBrandId(foundBrandId);

      // Fetch brand details
      const { data: brandData, error: brandError } = await supabase
        .from('brands')
        .select('*')
        .eq('id', foundBrandId)
        .single();

      if (brandError) throw brandError;

      setBrand(brandData);
      setBrandName(brandData.name || '');

      // Set brand image
      if (brandData.img) {
        setBrandImage(brandData.img);
        setImageError(false); // Reset error state when loading new image
      }

      // Parse banner color if it exists
      if (brandData.banner_color) {
        try {
          // Assume banner_color is stored as JSON: {color1: "#5efba9", color2: "#34D399"}
          // or as a string: "#5efba9,#34D399"
          const colorData = typeof brandData.banner_color === 'string' 
            ? JSON.parse(brandData.banner_color)
            : brandData.banner_color;
          
          if (colorData.color1) setColor1(colorData.color1);
          if (colorData.color2) setColor2(colorData.color2);
        } catch (e) {
          // If parsing fails, use defaults
          console.warn('Could not parse banner_color, using defaults');
        }
      }
    } catch (err) {
      console.error('Error fetching brand:', err);
      setError(err.message || (isArabic ? 'خطأ في جلب بيانات العلامة التجارية' : 'Error fetching brand data'));
    } finally {
      setLoading(false);
    }
  };

  const handleColor1Change = (e) => {
    setColor1(e.target.value);
    setSuccess(null);
  };

  const handleColor2Change = (e) => {
    setColor2(e.target.value);
    setSuccess(null);
  };

  const handleColor1TextChange = (e) => {
    const value = e.target.value;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
      setColor1(value || defaultColor1);
      setSuccess(null);
    }
  };

  const handleColor2TextChange = (e) => {
    const value = e.target.value;
    if (/^#[0-9A-Fa-f]{0,6}$/.test(value) || value === '') {
      setColor2(value || defaultColor2);
      setSuccess(null);
    }
  };

  const handleBrandNameChange = (e) => {
    setBrandName(e.target.value);
    setSuccess(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    setError(null);

    try {
      // Generate unique filename - ensure we have a valid extension
      let fileExt = file.name.split('.').pop();
      if (!fileExt || fileExt.length === 0) {
        // Fallback based on file type
        if (file.type.includes('jpeg') || file.type.includes('jpg')) fileExt = 'jpg';
        else if (file.type.includes('png')) fileExt = 'png';
        else if (file.type.includes('gif')) fileExt = 'gif';
        else if (file.type.includes('webp')) fileExt = 'webp';
        else fileExt = 'jpg'; // Default fallback
      }

      const randomSuffix = Math.random().toString(36).substring(2, 9);
      const fileName = `brand_${Date.now()}_${randomSuffix}.${fileExt}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('brands')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        setError(isArabic ? 'فشل رفع الصورة: ' + uploadError.message : 'Failed to upload image: ' + uploadError.message);
        return;
      }

      const filePath = uploadData?.path || fileName;

      // Try signed URL first (for private buckets)
      const { data: signedUrlData, error: signedUrlError } = await supabase.storage
        .from('brands')
        .createSignedUrl(filePath, 31536000); // 1 year expiration

      let imageUrl;

      if (signedUrlError) {
        // Fallback: try public URL (in case bucket is public)
        const { data: urlData } = supabase.storage
          .from('brands')
          .getPublicUrl(filePath);

        if (urlData?.publicUrl) {
          imageUrl = urlData.publicUrl;
        } else {
          setError(isArabic ? 'فشل إنشاء رابط الصورة' : 'Failed to generate image URL');
          return;
        }
      } else {
        imageUrl = signedUrlData?.signedUrl;
      }

      if (!imageUrl) {
        setError(isArabic ? 'فشل إنشاء رابط الصورة' : 'Failed to generate image URL');
        return;
      }

      // Set the image URL
      setBrandImage(imageUrl.trim());
      setImageError(false); // Reset error state when new image is uploaded
      setSuccess(isArabic ? 'تم رفع الصورة بنجاح!' : 'Image uploaded successfully!');
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || (isArabic ? 'خطأ في رفع الصورة' : 'Error uploading image'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setBrandImage('');
    setImageError(false);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!brandId) {
      setError(isArabic ? 'لم يتم العثور على معرف العلامة التجارية' : 'Brand ID not found');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Store colors as JSON object
      const bannerColor = {
        color1: color1 || defaultColor1,
        color2: color2 || defaultColor2
      };

      // Prepare update data
      const updateData = {
        banner_color: JSON.stringify(bannerColor),
        name: brandName.trim()
      };

      // Only update image if it has changed
      if (brandImage !== brand?.img) {
        updateData.img = brandImage || null;
      }

      const { error: updateError } = await supabase
        .from('brands')
        .update(updateData)
        .eq('id', brandId);

      if (updateError) throw updateError;

      setSuccess(isArabic ? 'تم حفظ التغييرات بنجاح!' : 'Changes saved successfully!');
      
      // Update local brand state
      setBrand({ ...brand, name: brandName.trim(), banner_color: JSON.stringify(bannerColor), img: brandImage || brand?.img });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving changes:', err);
      setError(err.message || (isArabic ? 'خطأ في حفظ التغييرات' : 'Error saving changes'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setColor1(defaultColor1);
    setColor2(defaultColor2);
    setBrandImage(brand?.img || '');
    setBrandName(brand?.name || '');
    setSuccess(null);
    setError(null);
  };

  const getGradient = () => {
    return `linear-gradient(135deg, ${color1 || defaultColor1} 0%, ${color2 || defaultColor2} 100%)`;
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <div>
            <Title>{isArabic ? 'إعدادات العلامة التجارية' : 'Brand Settings'}</Title>
            <Subtitle>{isArabic ? 'تخصيص ألوان البانر' : 'Customize banner colors'}</Subtitle>
          </div>
        </Header>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        </Card>
      </Container>
    );
  }

  if (!brand) {
    return (
      <Container>
        <Header>
          <div>
            <Title>{isArabic ? 'إعدادات العلامة التجارية' : 'Brand Settings'}</Title>
            <Subtitle>{isArabic ? 'تخصيص ألوان البانر' : 'Customize banner colors'}</Subtitle>
          </div>
        </Header>
        <Card>
          <ErrorMessage>
            {error || (isArabic ? 'لم يتم العثور على علامة تجارية' : 'No brand found')}
          </ErrorMessage>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div>
          <Title>{isArabic ? 'إعدادات العلامة التجارية' : 'Brand Settings'}</Title>
          <Subtitle>{isArabic ? 'تخصيص ألوان البانر لصفحة العلامة التجارية' : 'Customize banner colors for your brand page'}</Subtitle>
        </div>
      </Header>

      <Card>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <FormGroup>
          <Label>{isArabic ? 'اسم العلامة التجارية' : 'Brand Name'}</Label>
          <TextInput
            type="text"
            value={brandName}
            onChange={handleBrandNameChange}
            placeholder={isArabic ? 'أدخل اسم العلامة التجارية' : 'Enter brand name'}
          />
          <InfoText>
            {isArabic 
              ? 'سيتم عرض هذا الاسم في صفحة العلامة التجارية والبانر.'
              : 'This name will be displayed on your brand page and banner.'}
          </InfoText>
        </FormGroup>

        <ImageUploadSection>
          <Label>{isArabic ? 'صورة العلامة التجارية' : 'Brand Image'}</Label>
          <ImagePreviewContainer>
            <ImagePreview>
              {brandImage && !imageError ? (
                <>
                  <PreviewImage 
                    src={brandImage} 
                    alt={brand?.name || 'Brand'} 
                    onError={() => {
                      setImageError(true);
                    }} 
                  />
                  <RemoveImageButton onClick={handleRemoveImage} title={isArabic ? 'إزالة الصورة' : 'Remove image'}>
                    <FiX />
                  </RemoveImageButton>
                </>
              ) : (
                <ImagePlaceholder>
                  <FiImage />
                </ImagePlaceholder>
              )}
            </ImagePreview>
            <UploadButton>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileSelect}
                disabled={uploadingImage}
              />
              {uploadingImage ? (
                <>
                  <LoadingSpinner />
                  {isArabic ? 'جاري الرفع...' : 'Uploading...'}
                </>
              ) : (
                <>
                  <FiUpload />
                  {isArabic ? 'رفع صورة جديدة' : 'Upload New Image'}
                </>
              )}
            </UploadButton>
            <InfoText>
              {isArabic 
                ? 'سيتم عرض هذه الصورة في صفحة العلامة التجارية.'
                : 'This image will be displayed on your brand page.'}
            </InfoText>
          </ImagePreviewContainer>
        </ImageUploadSection>

        <FormGroup>
          <Label>{isArabic ? 'ألوان البانر' : 'Banner Colors'}</Label>
          <ColorInputGroup>
            <ColorInputWrapper>
              <ColorInputLabel>{isArabic ? 'اللون الأول (البداية)' : 'Color 1 (Start)'}</ColorInputLabel>
              <ColorPickerContainer>
                <ColorInput
                  type="color"
                  value={color1}
                  onChange={handleColor1Change}
                />
                <ColorTextInput
                  type="text"
                  value={color1}
                  onChange={handleColor1TextChange}
                  placeholder="#5efba9"
                  maxLength={7}
                />
              </ColorPickerContainer>
            </ColorInputWrapper>
            <ColorInputWrapper>
              <ColorInputLabel>{isArabic ? 'اللون الثاني (النهاية)' : 'Color 2 (End)'}</ColorInputLabel>
              <ColorPickerContainer>
                <ColorInput
                  type="color"
                  value={color2}
                  onChange={handleColor2Change}
                />
                <ColorTextInput
                  type="text"
                  value={color2}
                  onChange={handleColor2TextChange}
                  placeholder="#34D399"
                  maxLength={7}
                />
              </ColorPickerContainer>
            </ColorInputWrapper>
          </ColorInputGroup>
          <InfoText>
            {isArabic 
              ? 'سيتم استخدام هذه الألوان لإنشاء تدرج لوني في بانر صفحة العلامة التجارية'
              : 'These colors will be used to create a gradient in your brand page banner'}
          </InfoText>
        </FormGroup>

        <PreviewSection>
          <PreviewLabel>{isArabic ? 'معاينة البانر' : 'Banner Preview'}</PreviewLabel>
          <PreviewBanner $gradient={getGradient()}>
            <PreviewBrandName>{brandName || brand?.name || 'Brand Name'}</PreviewBrandName>
            <PreviewDescription>
              {isArabic ? 'معاينة البانر مع الألوان المحددة' : 'Banner preview with selected colors'}
            </PreviewDescription>
          </PreviewBanner>
        </PreviewSection>

        <ButtonGroup>
          <SaveButton onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <LoadingSpinner />
                {isArabic ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              <>
                <FiSave />
                {isArabic ? 'حفظ التغييرات' : 'Save Changes'}
              </>
            )}
          </SaveButton>
          <ResetButton onClick={handleReset}>
            <FiRefreshCw />
            {isArabic ? 'إعادة تعيين' : 'Reset to Default'}
          </ResetButton>
        </ButtonGroup>
      </Card>
    </Container>
  );
}

export default AdminBrandSettings;

